import { ApiResponse, PaginatedResponse } from '../../../types';
import { api, unwrapData } from '../../../app/store/api';
import { Withdraw } from '../../gateways/types/withdraw.types';
import {
    AutoWithdraw,
    WithdrawPspBank,
    WithdrawRequest,
    WithdrawTransaction,
} from '../../gateways/types/withdraw.types';

export const withdrawApi = api.injectEndpoints({
    endpoints: (builder) => ({
        withdraw: builder.query<Withdraw, { workspaceId: number; id: string }>({
            transformResponse: unwrapData,
            query: ({ workspaceId, id }) => ({
                url: `workspaces/${workspaceId}/withdrawals/${id}`,
                method: 'GET',
            }),
        }),
        updatedAutoWithdraw: builder.mutation<
            AutoWithdraw | void,
            (Pick<AutoWithdraw, 'id' | 'amount' | 'period' | 'bank_account'> & {
                workspaceId: number;
                type: '0' | '1';
                partners?: unknown;
            }) | { workspaceId: number; type: '0' }
        >({
            transformResponse: unwrapData,
            query: ({ workspaceId, type, ...body }) => ({
                url: `workspaces/${workspaceId}/withdrawals/automatic`,
                method: type === '1' ? 'PATCH' : 'DELETE',
                ...(type === '1' && { body }),
            }),
        }),
        getAutoWithdraw: builder.query<AutoWithdraw | null, number>({
            transformResponse: unwrapData,
            query: (workspaceId) => ({
                url: `workspaces/${workspaceId}/withdrawals/automatic`,
                method: 'GET',
            }),
        }),
        requestManualWithdraw: builder.mutation<
            Withdraw,
            WithdrawRequest & { workspaceId: number }
        >({
            transformResponse: unwrapData,
            query: ({ workspaceId, ...body }) => ({
                url: `workspaces/${workspaceId}/withdrawals`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'Withdraw' }] : []),
        }),
        requestPspBanksWithdraw: builder.query<WithdrawPspBank, WithdrawPspBank & { workspaceId: number }>({
            transformResponse: unwrapData,
            query: ({ workspaceId, ...params }) => ({
                url: `workspaces/${workspaceId}/withdrawals/psp-options`,
                method: 'GET',
                params,
            }),
        }),
        exportWithdraws: builder.mutation<
            Blob,
            {
                workspaceId: number;
                page: number;
                perPage: number;
                search?: any;
            }
        >({
            query: ({ workspaceId, page, perPage, search }) => ({
                url: `workspaces/${workspaceId}/withdrawals/export`,
                method: 'GET',
                params: {
                    page,
                    per_page: perPage,
                    ...search,
                },
                responseHandler: (response) => response.blob(),
            }),
        }),
        withdraws: builder.query<
            PaginatedResponse<Withdraw>,
            {
                workspaceId: number;
                page?: number;
                perPage?: number;
                search?: any;
            }
        >({
            transformResponse: unwrapData,
            query: ({ workspaceId, page, perPage = 15, search }) => {
                return {
                    url: `workspaces/${workspaceId}/withdrawals`,
                    method: 'GET',
                    params: {
                        ...(perPage && { per_page: perPage }),
                        ...(page && { page }),
                        ...search,
                    },
                };
            },
            providesTags: ['Withdraw'],
        }),
        withdrawTransactionList: builder.query<
            PaginatedResponse<unknown>,
            {
                workspaceId: number;
                page?: number;
                perPage?: number;
                withdraw_id: number;
            }
        >({
            transformResponse: unwrapData,
            query: ({ workspaceId, page, perPage = 15, withdraw_id }) => ({
                url: `workspaces/${workspaceId}/withdrawals/${withdraw_id}/transactions`,
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                },
            }),
            providesTags: ['Withdraw'],
        }),
        getWithdrawTransaction: builder.query<
            WithdrawTransaction,
            number
        >({
            transformResponse: unwrapData,
            query: (workspaceId) => ({
                url: `workspaces/${workspaceId}/transactions/withdrawable`,
                method: 'GET',
            }),
            providesTags: ['Withdraw'],
        }),
    }),
});

export const {
    useWithdrawQuery,
    useGetWithdrawTransactionQuery,
    useWithdrawsQuery,
    useExportWithdrawsMutation,
    useRequestManualWithdrawMutation,
    useLazyRequestPspBanksWithdrawQuery,
    useUpdatedAutoWithdrawMutation,
    useGetAutoWithdrawQuery,
    useWithdrawTransactionListQuery,
} = withdrawApi;

export const useRequestPspBanksWithdrawQuery =
    useLazyRequestPspBanksWithdrawQuery;
